import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import {
  formKeyMap,
  ValidateField,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { html, Html } from 'rune-ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import {
  PureInput,
  pureInputVariant,
} from '../../../../../../../../shared/rune-component/Input/PureInput.ts';
import { embedConfigs } from './embed-service.ts';
import { unEscaper } from '../../../../../../../../shared/utils/escape.ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import klass from './Input_ProductContent.module.scss';
import {
  EmbedServiceName,
  ProductStory,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/types.ts';
import { ImageUploader } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/views/ImageUploader/ImageUploader.ts';
import { Alert } from '@services/MarppleShop/shared/components/cells/Alert/Alert.ts';
import { noop } from '@fxts/core';

export interface InputProductContentData<T extends MODE> extends InputEditor {
  value: ProductStory;
  store: ReturnType<typeof createCreatorProductStore<T>>;
}

export class Input_ProductContent<T extends MODE> extends Input_Base<InputProductContentData<T>> {
  protected readonly store = this.data.store;

  private makeEmbedPreviewHtml(serviceName: EmbedServiceName, url: string): Html {
    setTimeout(() => this.processEmbedPreview(serviceName, url), 0);
    return html`<div class="${klass.embed_preview}" data-service="${serviceName}"></div>`;
  }

  private async processEmbedPreview(serviceName: EmbedServiceName, url: string): Promise<void> {
    const previewElement = this.element().querySelector(
      `.${klass.embed_preview}[data-service="${serviceName}"]`,
    ) as HTMLElement;

    if (!previewElement) return;

    try {
      const config = embedConfigs[serviceName];
      const embedHtml = await config.process(url);

      if (embedHtml) {
        previewElement.innerHTML = embedHtml;
        this.processEmbedWidgets(serviceName);
      }
    } catch (error) {
      console.error(`${serviceName} embed 오류:`, error);
    }
  }

  private processEmbedWidgets(serviceName: EmbedServiceName): void {
    setTimeout(() => {
      switch (serviceName) {
        case 'instagram':
          if (window.instgrm?.Embeds) {
            window.instgrm.Embeds.process();
          }
          break;
        case 'x':
          if (window.twttr?.widgets) {
            const tweetContainer = this.element().querySelector('.tw-embed');
            if (tweetContainer) {
              const tweetId = tweetContainer.getAttribute('data-tweet-id');
              if (tweetId) {
                window.twttr.widgets.createTweet(tweetId, tweetContainer);
              }
            }
          }
          break;
        case 'facebook':
          if (window.FB?.XFBML) {
            window.FB.XFBML.parse(this.element());
          }
          break;
      }
    }, 100);
  }

  private handleIstagramEmbed(event: MessageEvent) {
    if (event.origin.indexOf('.instagram.com') === -1) {
      return;
    }

    try {
      const data = JSON.parse(event.data);
      if (data.details && data.details.height) {
        const instagramContainer = this.element().querySelector<HTMLElement>(
          `[data-service="${embedConfigs.instagram.name}"]`,
        );

        if (instagramContainer) {
          instagramContainer.style.height = `${data.details.height}px`;
        }
      }
    } catch (error) {
      console.error(`${embedConfigs.instagram.name} handleIstagramEmbed 오류:`, error);
    }
  }

  protected inputTemplate(): Html {
    return html`
      <div class="${klass.root}">
        ${this.makeTitleAndContentHtml()}
        <div class="${klass.embed_wrap}">
          ${Object.values(embedConfigs).map((config) => this.makeEmbedHtml(config))}
        </div>
        ${new ImageUploader({
          images: this.store.formData?._?.product?._?.product_story?._?.product_story_images,
          onChange: (images) => {
            this.store.actions.setField(formKeyMap.product_story_images, images);
          },
        })}
      </div>
    `;
  }

  protected makeTitleAndContentHtml(): Html {
    return html`
      <div class="${klass.content_wrap}">
        ${new PureInput({
          variant: pureInputVariant.studio_product_content,
          name: 'title',
          value: unEscaper(this.store.formData?._?.product?._?.product_story?.title ?? ''),
          onFocusout: (valueObj) => {
            this.updateProductStory({ title: valueObj.getValue() });
          },
          placeholder: '제목을 작성해주세요.',
          min_rows: 1,
          max_rows: 5,
          font_klass: shareFont('24_bold'),
        })}
        ${new PureInput({
          variant: pureInputVariant.studio_product_content,
          name: 'content',
          value: unEscaper(this.store.formData?._?.product?._?.product_story?.description ?? ''),
          onFocusout: (valueObj) => {
            this.updateProductStory({ description: valueObj.getValue() });
          },
          placeholder: '내용을 작성해주세요.',
          min_rows: 1,
          max_rows: 1000,
          font_klass: shareFont('14_medium'),
        })}
      </div>
    `;
  }

  protected makeEmbedHtml(config: (typeof embedConfigs)[EmbedServiceName]): Html {
    const fieldMapping = {
      youtube: 'youtube_embed',
      instagram: 'ins_embed',
      x: 'tw_embed',
      facebook: 'fb_embed',
      tiktok: 'tiktok_embed',
    } as const;

    const fieldName = fieldMapping[config.name];
    const currentValue = this.store.formData._.product._.product_story[fieldName] ?? '';

    return html`
      <div class="${klass.embed_service}">
        <div class="${klass.embed_input}">
          <div class="${klass.embed_icon_wrap}">
            <img src="${config.img_url}" alt="${config.name} icon" />
          </div>
          ${new PureInput({
            variant: pureInputVariant.studio_editor,
            name: config.name,
            value: unEscaper(currentValue),
            onFocusout: async (valueObj) => {
              const url = valueObj.getValue();

              if (url && config.validate && !config.validate(url)) {
                valueObj.setValue('');

                await Alert.open({
                  title: 'URL 형식 오류',
                  message: `올바른 ${config.name} URL을 입력해주세요.`,
                });
                return;
              }

              this.updateProductStory({ [fieldName]: valueObj.getValue() });
              this.redraw();
            },
            placeholder: config.desc,
          })}
        </div>
        ${currentValue ? this.makeEmbedPreviewHtml(config.name, currentValue) : ''}
      </div>
    `;
  }

  protected updateProductStory(updates: Partial<ProductStory>): void {
    this.store.actions.setField(this.data.name, {
      ...this.store.formData._.product._.product_story,
      ...updates,
    });
  }

  public override getName(): ValidateField {
    return this.data.name;
  }

  public override getValue() {
    return this.store.formData._.product._.product_story;
  }

  public override setError() {
    noop();
  }

  protected override onMount() {
    window.addEventListener('message', this.handleIstagramEmbed.bind(this));
  }

  protected override onUnmount() {
    window.removeEventListener('message', this.handleIstagramEmbed.bind(this));
  }
}
